/**
 * Sweet state store for processed comments data.
 * This store should ONLY be updated by CommentsDataBackgroundLoader.
 * All other consumers should use the state/actions hooks to read data.
 */

import {
	createStore,
	createHook,
	createActionsHook,
	createStateHook,
	defaultRegistry,
} from 'react-sweet-state';
import type { StoreActionApi } from 'react-sweet-state';

import type { Comment, CommentReply } from './useAllCommentsData.ts';
import type { CommentsDataMap } from './commentDataManagerUtils.ts';

/**
 * State type for processed comments data.
 * This represents the categorized and filtered comments data ready for consumption.
 */
export type ProcessedCommentsDataState = {
	allComments: Comment[];
	allFilteredComments: Comment[];
	openInlineComments: Comment[];
	openFooterComments: Comment[];
	resolvedComments: Comment[];
	openComments: Comment[];
	commentsDataMap: CommentsDataMap;
	flatCommentsMap: Record<string, Comment | CommentReply>;
	flatMarkerRefMap: Record<string, Comment>;
	hasNestedComments: boolean;
	isDataProcessed: boolean;
	commentCount: number | null;
	hasAnyComments: boolean;
	error: Error | undefined;
	refetch: (() => Promise<any>) | undefined;
};

/**
 * Returns the initial state for the processed comments data store.
 */
export const getInitialState = (): ProcessedCommentsDataState => ({
	allComments: [],
	allFilteredComments: [],
	openInlineComments: [],
	openFooterComments: [],
	resolvedComments: [],
	openComments: [],
	commentsDataMap: { inline: {}, general: {} },
	flatCommentsMap: {},
	flatMarkerRefMap: {},
	hasNestedComments: false,
	isDataProcessed: false,
	commentCount: 0,
	hasAnyComments: false,
	error: undefined,
	refetch: undefined,
});

/**
 * Actions for managing processed comments data.
 * NOTE: setProcessedData should ONLY be called by CommentsDataBackgroundLoader.
 */
export const actions = {
	/**
	 * Updates all processed comments data.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 * Note: refetch is set separately via setRefetch, so it's not included here.
	 */
	setProcessedData:
		(data: Omit<ProcessedCommentsDataState, 'error' | 'refetch'>) =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			const currentState = getState();
			setState({
				...data,
				error: undefined,
				// Keep existing refetch if it exists, don't overwrite it
				refetch: currentState.refetch,
			});
		},

	/**
	 * Sets an error state for processed comments data.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 */
	setError:
		(error: Error) =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			const currentState = getState();
			setState({
				...currentState,
				error,
				isDataProcessed: true,
			});
		},

	/**
	 * Sets the refetch function from useAllCommentsData.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 */
	setRefetch:
		(refetch: () => Promise<any>) =>
		({ setState, getState }: StoreActionApi<ProcessedCommentsDataState>) => {
			const currentState = getState();
			setState({
				...currentState,
				refetch,
			});
		},

	/**
	 * Clears all processed comments data and resets to initial state.
	 * WARNING: This should ONLY be called by CommentsDataBackgroundLoader.
	 */
	clearProcessedData:
		() =>
		({ setState }: StoreActionApi<ProcessedCommentsDataState>) => {
			setState(getInitialState());
		},
};

/**
 * Sweet state store for processed comments data.
 * This is the single source of truth for processed/categorized comments.
 */
export const ProcessedCommentsDataStore = createStore({
	initialState: getInitialState(),
	actions,
	name: 'ProcessedCommentsDataStore',
});

/**
 * Hook to access both state and actions for processed comments data.
 * Most consumers should use useProcessedCommentsDataState instead.
 */
export const useProcessedCommentsData = createHook(ProcessedCommentsDataStore);

/**
 * Hook to access actions for processed comments data.
 * WARNING: These actions should ONLY be used by CommentsDataBackgroundLoader.
 */
export const useProcessedCommentsDataActions = createActionsHook(ProcessedCommentsDataStore);

/**
 * Hook to access state for processed comments data.
 * This is the recommended hook for most consumers.
 */
export const useProcessedCommentsDataState = createStateHook(ProcessedCommentsDataStore);

// Non-React utility for accessing store outside of React components
const processedCommentsDataStoreInstance = defaultRegistry.getStore(ProcessedCommentsDataStore);

/**
 * Gets the current processed comments data state outside of React components.
 * This function can be called from anywhere to access the current state.
 * Uses the store instance from defaultRegistry to get the latest state.
 */
export const getProcessedCommentsDataState = (): ProcessedCommentsDataState => {
	return processedCommentsDataStoreInstance.storeState.getState();
};
